import React from 'react';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';

/**
 * Shared auth-flow shell: teal→sky wash with a bottom wave horizon.
 * Light: near-white → cyan → sky. Dark: near-black → deep teal → cyan glow.
 *
 * Decorative layers use DOM order (not isolation / content z-index) so
 * in-tree fixed Snackbars can still stack above the app Header.
 */
const AuthPageBackground = ({ children, sx = {} }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        overflow: 'hidden',
        background: isDark
          ? 'linear-gradient(180deg, #0A1214 0%, #0F1C1F 40%, #12343B 72%, #006978 100%)'
          : 'linear-gradient(180deg, #F7FCFD 0%, #E8F7FA 45%, #B8E8F0 78%, #5BAEE6 100%)',
        ...sx,
      }}
    >
      {/* Soft depth blobs — painted first, behind in-flow content */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: isDark
            ? `
              radial-gradient(ellipse 70% 45% at 12% 88%, rgba(0, 188, 212, 0.22) 0%, transparent 70%),
              radial-gradient(ellipse 55% 40% at 92% 92%, rgba(255, 152, 0, 0.1) 0%, transparent 65%)
            `
            : `
              radial-gradient(ellipse 70% 45% at 12% 88%, rgba(0, 188, 212, 0.28) 0%, transparent 70%),
              radial-gradient(ellipse 55% 40% at 92% 92%, rgba(255, 152, 0, 0.12) 0%, transparent 65%)
            `,
        }}
      />

      {/* Bottom horizon wave */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          height: { xs: '16vh', sm: '20vh', md: '26vh' },
          maxHeight: 220,
          minHeight: 96,
          lineHeight: 0,
          '@media (max-height: 640px)': {
            height: '12vh',
            minHeight: 64,
            maxHeight: 120,
          },
        }}
      >
        <Box
          component="svg"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 220"
          preserveAspectRatio="none"
          sx={{ width: '100%', height: '100%', display: 'block' }}
        >
          <path
            fill={isDark ? '#00BCD4' : '#0097A7'}
            fillOpacity={isDark ? 0.28 : 0.35}
            d="M0,96L80,106.7C160,117,320,139,480,138.7C640,139,800,117,960,112C1120,107,1280,117,1360,122.7L1440,128L1440,220L1360,220C1280,220,1120,220,960,220C800,220,640,220,480,220C320,220,160,220,80,220L0,220Z"
          />
          <path
            fill={isDark ? '#4DD0E1' : '#00BCD4'}
            fillOpacity={isDark ? 0.22 : 0.5}
            d="M0,140L80,149.3C160,159,320,177,480,170.7C640,165,800,133,960,128C1120,123,1280,149,1360,162.7L1440,176L1440,220L1360,220C1280,220,1120,220,960,220C800,220,640,220,480,220C320,220,160,220,80,220L0,220Z"
          />
        </Box>
      </Box>

      {children}
    </Box>
  );
};

AuthPageBackground.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
};

export default AuthPageBackground;
